import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * True once the observed element is within `marginVh` viewport-heights of the
 * visible area — stays true afterward (one-shot, not a repeating toggle).
 *
 * This is `useInView`'s pattern (see that file's comment for why a plain
 * IntersectionObserver misses instant same-page jumps — anchor nav, End/Home,
 * scroll restoration — and why a `scroll`/`resize` rect fallback is needed
 * alongside it), widened for a different job: `useInView` answers "is this
 * on screen yet", which is right for a reveal animation. `LazySection` needs
 * to answer "will this be on screen soon", so its lazy chunk has time to
 * fetch, parse and mount before the reader actually arrives — that's what
 * `marginVh` buys, via the observer's `rootMargin` and a matching expansion
 * on the manual rect check.
 */
export function useApproach<T extends HTMLElement>(marginVh = 2): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [approaching, setApproaching] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || approaching) return;

    const margin = window.innerHeight * marginVh;
    const isNear = () => {
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight + margin && rect.bottom > -margin;
    };

    if (isNear()) {
      setApproaching(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setApproaching(true);
      },
      { rootMargin: `${margin}px 0px` },
    );
    observer.observe(el);

    const onScrollOrResize = () => {
      if (isNear()) setApproaching(true);
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [marginVh, approaching]);

  return [ref, approaching];
}
